import React, { useState, useEffect, useCallback, useRef } from "react";
import { BookOpenCheck, Edit2, Trash2, User } from "lucide-react";

import {
  AreaHighlight,
  TextHighlight,
  PdfHighlighter,
  PdfLoader,
  MonitoredHighlightContainer,
  useHighlightContainerContext,
} from "react-pdf-highlighter-extended";
import type {
  Content,
  GhostHighlight,
  ScaledPosition,
  ViewportHighlight,
} from "react-pdf-highlighter-extended";

import { QuestionPane } from "./QuestionPane";
import { AnswerPane } from "./AnswerPane";
import type { ExtendedHighlight } from "@/api/highlights";
import { useDocumentAnswers } from "@/api/q-and-a";
import { useUser } from "@/api/user";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import LoadingSpinner from "@/components/loading-spinner";
import { usePersistentHighlights } from "../hooks/usePersistentHighlights";
import { useCitationHighlights } from "../hooks/useCitationHighlights";
import { 
  filterQuestionsForDocument,
  findFirstCitationForGroupInDocument 
} from "../utils/answerGroupUtils";
import { searchTextInDocument } from "../utils/textSearch";
import type { HighlightMetaIndex } from "../types";
import type { PDFDocumentProxy } from "pdfjs-dist";

type DocumentFile = Blob | string;

interface PdfViewerProps {
  document: DocumentFile;
  pageNumber?: number;
  textToHighlight?: string;
  comment?: string;
  documentId?: string;
  readOnly?: boolean; // Disable Q&A and highlighting features
  target?: {
    instanceId: string; // Required: Project/Portfolio ID for answer / doc linking
    groupId?: string; // Optional: Answer Group ID for question/answer selection
    citationId?: string; // Optional: Citation ID to try to scroll directly to the citation
  };
  onTargetReached?: () => void; // Callback when target highlight is reached
}

const SCROLL_HIGHLIGHT_DURATION_MS = 3000;

// Textract often matches citations against the printed name in a signature
// block (e.g. "John Smith"), which sits on the row BELOW the actual
// handwritten signature. The default scroll-to-highlight lands the match near
// the top of the viewport, pushing the signature off-screen above. A small
// additional upward nudge keeps the row above the highlight in view so the
// signature itself is visible when the user jumps to the citation.
const SIGNATURE_CONTEXT_OFFSET_PX = 140;

// When the matched line IS itself the signature row (e.g. "Signature/Title",
// "Signed by ...", "Docusign Envelope ID: ...", "/s/ Name"), the handwritten
// mark sits on or beside it, not above it. Applying the upward offset in that
// case overshoots and leaves empty space below the highlight. Skip the offset
// when the matched text contains any of these markers.
const HIGHLIGHT_IS_SIGNATURE_ROW_RE =
  /\b(signature|signatory|signed|docusign|adobe\s+sign)|[/\\]s[/\\]?\s/i;

// Component for rendering individual highlights using context
const HighlightRenderer = ({
  citationDataMap,
  highlightMetaIndex,
  canEditHighlight,
  onDeleteClick,
  editHighlightComment,
  updateHighlight,
  selectedQuestionId,
  activeAnswerGroupId,
  activeCitationId,
  showAllHighlights,
}: {
  citationDataMap: Map<string, any>;
  highlightMetaIndex: HighlightMetaIndex;
  canEditHighlight: (id: string) => boolean;
  onDeleteClick: (id: string) => void;
  editHighlightComment: (id: string, comment: string) => Promise<void>;
  updateHighlight: (
    highlightId: string,
    position: Partial<ScaledPosition>,
    content: Partial<Content>,
  ) => Promise<void>;
  selectedQuestionId?: string;
  activeAnswerGroupId?: string;
  activeCitationId?: string;
  showAllHighlights: boolean;
}) => {
  const { highlight, isScrolledTo, highlightBindings } =
    useHighlightContainerContext<ExtendedHighlight>();

  // Flash animation state driven by isScrolledTo
  const [isFlashing, setIsFlashing] = useState(false);
  useEffect(() => {
    if (isScrolledTo) {
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), SCROLL_HIGHLIGHT_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isScrolledTo]);

  useEffect(() => {
    const container = highlightBindings?.container as HTMLElement | undefined;
    const textLayer = highlightBindings?.textLayer as HTMLElement | undefined;

    if (!container || !textLayer) return;

    const page = textLayer.closest(".page") as HTMLElement | null;
    if (!page) return;

    if (getComputedStyle(page).position === "static") {
      page.style.position = "relative";
    }

    let overlay = page.querySelector<HTMLDivElement>(":scope > .pdf-highlight-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "pdf-highlight-overlay";
      Object.assign(overlay.style, {
        position: "absolute",
        inset: "0",
        transform: "none",
        transformOrigin: "0 0",
        zIndex: "3",
        pointerEvents: "auto",
      });
      page.appendChild(overlay);
    }

    if (container.parentElement !== overlay) {
      overlay.appendChild(container);
    }

    container.style.transform = "none";
    container.style.transformOrigin = "0 0";
  }, [highlightBindings]);

  const isTextHighlight =
    highlight.type === "text" || highlight.type !== "area";

  // Determine highlight type for styling
  const isCitation =
    highlight.id.startsWith("citation-") ||
    highlight.id.startsWith("merged-citation-");

  // Tiered citation rendering: active > background > hidden
  // Visibility driven entirely by React state — isScrolledTo is only used for flash animation
  let wrapperClass: string;
  if (!isCitation) {
    wrapperClass = "user-highlight";
  } else {
    const meta = highlightMetaIndex.meta.get(highlight.id);
    const isActive = meta?.answerGroupIds?.includes(activeAnswerGroupId ?? "");
    const isSelectedQuestion = meta?.questionIds?.includes(selectedQuestionId ?? "");
    // When a specific citation is clicked (e.g. one signer row in a signature
    // Q&A where many signers belong to the same question), narrow Focus to
    // just that citation — hide every non-clicked citation regardless of
    // answer group or question, so "neighbor" signatures on the same page
    // stop rendering as background-faded.
    const isClickedCitation =
      !!activeCitationId && meta?.citationIds?.includes(activeCitationId);
    const narrowedByClick = !!activeCitationId && !isClickedCitation;

    if (narrowedByClick) {
      wrapperClass = "citation-highlight-hidden";
    } else if (isActive) {
      wrapperClass = "citation-highlight-active";
    } else if (isSelectedQuestion) {
      wrapperClass = "citation-highlight-background";
    } else if (showAllHighlights) {
      wrapperClass = "citation-highlight-background";
    } else {
      wrapperClass = "citation-highlight-hidden";
    }
  }

  // Add flash class when flashing
  const flashClass = isFlashing
    ? isCitation ? "highlight-flash-citation" : "highlight-flash-user"
    : "";

  const tooltip = (
    <HighlightPopup
      comment={highlight.comment || { text: "", emoji: "" }}
      highlight={highlight}
      citationData={citationDataMap.get(highlight.id)}
      canEdit={canEditHighlight(highlight.id)}
      onDelete={() => onDeleteClick(highlight.id)}
      onEdit={async (newComment) => {
        try {
          await editHighlightComment(highlight.id, newComment);
        } catch (error) {
          console.error("Failed to edit highlight:", error);
        }
      }}
    />
  );

  return (
    <div className={flashClass ? `${wrapperClass} ${flashClass}` : wrapperClass} data-highlight-id={highlight.id}>
      <MonitoredHighlightContainer
        highlightTip={{
          position: highlight.position,
          content: tooltip,
        }}
      >
        {isTextHighlight ? (
          <TextHighlight highlight={highlight} isScrolledTo={isScrolledTo} />
        ) : (
          <AreaHighlight
            highlight={highlight}
            isScrolledTo={isScrolledTo}
            onChange={(boundingRect) => {
              if (
                highlight.position?.rects &&
                Array.isArray(highlight.position.rects)
              ) {
                // Convert LTWHP to Scaled format for database storage
                const scaledRect = {
                  x1: boundingRect.left,
                  y1: boundingRect.top,
                  x2: boundingRect.left + boundingRect.width,
                  y2: boundingRect.top + boundingRect.height,
                  width: boundingRect.width,
                  height: boundingRect.height,
                  pageNumber: boundingRect.pageNumber,
                };

                // Update the highlight in the database with the new bounding rectangle
                updateHighlight(
                  highlight.id,
                  {
                    boundingRect: scaledRect,
                    rects: [scaledRect],
                  },
                  {}, // No content changes for area highlights
                );
              }
            }}
          />
        )}
      </MonitoredHighlightContainer>
    </div>
  );
};

// Helper function to render icons based on highlight type
const renderHighlightIcon = (iconType: string) => {
  const iconClass = "w-4 h-4 inline-block";
  switch (iconType) {
    case "citation":
    case "merged-citation":
      return <BookOpenCheck className={iconClass} />;
    case "user":
    case "manual":
      return <User className={iconClass} />;
    default:
      // Fallback for any legacy emoji strings
      if (iconType === "📍" || iconType === "❓") {
        return <BookOpenCheck className={iconClass} />;
      }
      return <User className={iconClass} />;
  }
};

const parseIdFromHash = () =>
  document.location.hash.slice("#highlight-".length);

const resetHash = () => {
  document.location.hash = "";
};

// Custom Tip component for creating highlights
const CustomTip = ({
  onOpen,
  onConfirm,
  onClose,
  compact,
  position,
}: {
  onOpen: () => void;
  onConfirm: (comment: { text: string; emoji: string }) => void;
  onClose: () => void;
  compact: boolean;
  position?: { x: number; y: number };
}) => {
  const [text, setText] = useState("");
  // Always empty emoji for manual highlights since we use icons instead
  const emoji = "";

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onConfirm({ text, emoji });
  };

  // Helper function to get proper positioning relative to the PDF viewer
  const getAbsolutePosition = () => {
    if (!position) return { top: "200px", left: "200px" };

    // Find the PDF viewer container to get its position
    const pdfContainer = document.querySelector(".PdfHighlighter");
    if (!pdfContainer) {
      return {
        top: `${position.y - 50}px`,
        left: `${position.x}px`,
      };
    }

    // Calculate position relative to viewport, accounting for container position
    // The position parameter is already in viewport coordinates from the selection
    const absoluteY = position.y;
    const absoluteX = position.x;

    // Ensure the tip stays within viewport bounds
    const buttonHeight = compact ? 40 : 180; // Estimate heights
    const buttonWidth = compact ? 120 : 280;

    // Position the tip above the selection with some padding
    const finalY = Math.max(10, absoluteY - (compact ? 50 : buttonHeight + 10));
    const finalX = Math.min(
      window.innerWidth - buttonWidth - 10,
      Math.max(10, absoluteX - buttonWidth / 2), // Center horizontally on selection
    );

    return {
      top: `${finalY}px`,
      left: `${finalX}px`,
    };
  };

  const absPosition = getAbsolutePosition();

  if (compact) {
    return (
      <div
        className="Tip__compact pointer-events-auto fixed z-[999999] min-w-[120px] cursor-pointer rounded-md bg-primary px-4 py-2 text-center text-sm font-medium text-primary-foreground shadow-lg transition-colors hover:bg-primary/90"
        style={{
          top: absPosition.top,
          left: absPosition.left,
          transform: "none", // Remove transform since we're calculating absolute position
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }}
      >
        Add highlight
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div
        className="Tip__card pointer-events-auto fixed z-[999999] min-w-[280px] max-w-[400px] rounded-md border bg-popover px-4 py-4 text-popover-foreground shadow-lg"
        style={{
          top: absPosition.top,
          left: absPosition.left,
          transform: "none", // Remove transform since we're calculating absolute position
        }}
      >
        <div>
          <textarea
            placeholder="Your comment..."
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
            className="mb-2 min-h-[60px] w-full rounded-md border border-input bg-background p-3 text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0"
          />
          <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span>User highlight</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            className="h-10 flex-1"
          >
            Cancel
          </Button>
          <Button type="submit" className="h-10 flex-1">
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};

// Popup component for displaying highlight comments
const HighlightPopup = ({
  comment,
  highlight,
  citationData,
  canEdit = false,
  onDelete,
  onEdit,
}: {
  comment: { text: string; emoji: string };
  highlight?: ViewportHighlight<ExtendedHighlight>;
  citationData?: {
    questionText: string;
    answerText: string;
    answerExplanation: string;
    questions?: string[];
    questionsAndAnswers?: Array<{ question: string; answer: string }>;
    groupedQuestionsAndAnswers?: Array<{
      question: string;
      answers: Array<{
        answerText: string;
        answerExplanation?: string;
        isCurrentValue?: boolean;
        isComposite?: boolean;
      }>;
    }>;
  };
  canEdit?: boolean;
  onDelete?: () => void;
  onEdit?: (newComment: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);

  if (!comment.text) return null;

  const isMerged = highlight?.id.startsWith("merged-citation-");
  const isCitation = highlight?.id.startsWith("citation-") || isMerged;

  const handleEdit = () => {
    setEditText(comment.text);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (onEdit && editText.trim()) {
      onEdit(editText.trim());
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };

  return (
    <div className="Highlight__popup max-h-none min-w-[250px] max-w-[400px] overflow-visible break-words rounded-md border bg-popover px-4 py-3 text-sm text-popover-foreground shadow-md">
      <div className="flex items-start gap-2">
        {renderHighlightIcon(
          highlight?.id?.startsWith("citation-") ||
            highlight?.id?.startsWith("merged-citation-")
            ? "citation"
            : "user",
        )}
        <div className="min-w-0 flex-1">
          {/* Show user name for user highlights */}
          {!isCitation && highlight?.userInfo?.name && (
            <div className="mb-1 text-xs text-muted-foreground">
              {highlight.userInfo!.name}
            </div>
          )}
          <div className="break-words">
            {isEditing && canEdit && !isCitation ? (
              <div className="space-y-2">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full rounded border border-input bg-background p-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  rows={3}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    className="h-7 px-2 text-xs"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="h-7 px-2 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <span className="break-words">{comment.text}</span>{" "}
                {/* Show answer for single citations (simplified) */}
                {!isMerged && isCitation && citationData && (
                  <span className="break-words text-muted-foreground">
                    {" "}
                    - {citationData.answerText}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {/* Edit and Delete buttons for editable user highlights */}
        {canEdit && !isCitation && !isEditing && (
          <div className="flex gap-1 self-start">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              title="Edit highlight"
            >
              <Edit2 size={12} />
            </Button>
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                title="Delete highlight"
              >
                <Trash2 size={12} />
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Show questions with answers for merged citations */}
      {isMerged &&
        (citationData?.groupedQuestionsAndAnswers ||
          citationData?.questionsAndAnswers) && (
          <div className="mt-2 border-t border-border pt-1 text-xs">
            <div className="mb-1 font-medium text-foreground">
              Questions & Answers:
            </div>
            <div className="space-y-3">
              {/* Use new grouped format if available, fallback to old format */}
              {citationData.groupedQuestionsAndAnswers ? (
                citationData.groupedQuestionsAndAnswers.map(
                  (questionGroup, questionIndex) => (
                    <div key={questionIndex} className="break-words">
                      <div className="mb-1 font-medium text-foreground">
                        {questionGroup.question}
                      </div>
                      <ul className="ml-4 space-y-1">
                        {questionGroup.answers.map((answer, answerIndex) => (
                          <li key={answerIndex} className="break-words pl-2">
                            <div className="flex items-start gap-1">
                              <span className="text-muted-foreground">•</span>
                              <div className="flex-1">
                                <span
                                  className={
                                    answer.isCurrentValue
                                      ? "font-semibold text-foreground"
                                      : "text-muted-foreground"
                                  }
                                >
                                  {answer.answerText}
                                  {answer.isComposite && (
                                    <span className="ml-1 text-xs text-muted-foreground">
                                      (composite)
                                    </span>
                                  )}
                                </span>
                                {answer.isCurrentValue && (
                                  <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">
                                    Current Value
                                  </span>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                )
              ) : (
                /* Fallback to old format for backward compatibility */
                <ul className="ml-4 list-disc space-y-2 pl-0">
                  {citationData.questionsAndAnswers?.map((qa, index) => (
                    <li key={index} className="break-words pl-2">
                      <span className="break-words">{qa.question}</span>
                      <span className="break-words text-muted-foreground">
                        {" "}
                        - {qa.answer}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export function PdfViewer({
  document: documentFile,
  documentId,
  target,
  onTargetReached,
  readOnly,
}: PdfViewerProps) {
  const [url] = useState<string>(() => {
    if (typeof documentFile === "string") return documentFile;
    return URL.createObjectURL(documentFile);
  });

  // PDF.js worker configuration with fallback
  const workerSrc = "/pdf.worker.min.mjs";

  const scrollToHighlight = useRef<
    ((highlight: ExtendedHighlight) => void) | null
  >(null);
  const highlightsRef = useRef<ExtendedHighlight[]>([]);

  const [pdfDocumentLoaded, setPdfDocumentLoaded] = useState(false);

  // Fetch document Q&A data
  const { data: documentAnswersData } = useDocumentAnswers({
    documentId: documentId || "",
  });

  // Pre-filter questions to only include data for this specific document
  const documentAnswers = React.useMemo(() => {
    const rawData = documentAnswersData || [];
    return filterQuestionsForDocument(rawData, documentId);
  }, [documentAnswersData, documentId]);

  // PDF document reference for text search
  const pdfDocumentRef = useRef<PDFDocumentProxy>();

  // Text search functionality
  const searchText = useCallback(
    async (
      text: string,
      specificPage?: number | null,
    ): Promise<ExtendedHighlight[]> => {
      return searchTextInDocument(pdfDocumentRef.current, text, specificPage);
    },
    [],
  );

  const setPdfDocument = useCallback((pdfDocument: PDFDocumentProxy) => {
    pdfDocumentRef.current = pdfDocument;
  }, []);
  const { citationHighlights, citationDataMap, highlightMetaIndex } = useCitationHighlights(
    documentAnswersData,
    pdfDocumentLoaded,
    searchText,
    documentId,
  );

  // State for managing the tip
  const [showTip, setShowTip] = useState(false);
  const [tipCompact, setTipCompact] = useState(true);
  const [currentSelection, setCurrentSelection] = useState<{
    position: ScaledPosition;
    content: Content;
    hideTipAndSelection: () => void;
  } | null>(null);

  // State for Q&A panes
  const [showQuestionPane, setShowQuestionPane] = useState(true);
  const [showAnswerPane, setShowAnswerPane] = useState(true);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>();
  const [activeAnswerGroupId, setActiveAnswerGroupId] = useState<string>();
  const [activeCitationId, setActiveCitationId] = useState<string>();
  const [showAllHighlights, setShowAllHighlights] = useState(false);

  // Auto-set active answer group to first group (prefer is_current_value) when question changes
  React.useEffect(() => {
    if (!selectedQuestionId) {
      setActiveAnswerGroupId(undefined);
      return;
    }
    const question = documentAnswers.find((q) => q.question_id === selectedQuestionId);
    if (!question) return;
    const currentValueGroup = question.answer_groups.find((g) => g.is_current_value);
    setActiveAnswerGroupId(currentValueGroup?.id ?? question.answer_groups[0]?.id);
  }, [selectedQuestionId, documentAnswers]);

  // Drop the specific-citation Focus narrowing when the user switches questions.
  // Keyed on selectedQuestionId only — `documentAnswers` is a new array
  // reference on each parent render and would otherwise clobber the user's
  // click state on every re-render.
  React.useEffect(() => {
    setActiveCitationId(undefined);
  }, [selectedQuestionId]);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [highlightToDelete, setHighlightToDelete] = useState<string | null>(
    null,
  );

  // Get current user information
  const { data: currentUser } = useUser();

  // Persistent user highlights
  const {
    highlights: persistentHighlights,
    addHighlight: savePersistentHighlight,
    updateHighlight: updatePersistentHighlight,
    deleteHighlight: deletePersistentHighlight,
    canEditHighlight,
  } = usePersistentHighlights(documentId || "", currentUser?.id);

  // Combined highlights: citation highlights + persistent user highlights
  const highlights = React.useMemo(() => {
    const combined = [...citationHighlights, ...persistentHighlights];
    return combined;
  }, [citationHighlights, persistentHighlights]);

  // Keep highlights ref updated for callbacks
  React.useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);

  // Guard to prevent re-scrolling on subsequent renders after target is reached
  const targetReachedRef = useRef(false);

  // Reset the guard when target changes
  React.useEffect(() => {
    targetReachedRef.current = false;
  }, [target?.groupId, target?.citationId]);

  // Citation and question targeting: handle question selection and citation scrolling
  React.useEffect(() => {
    if (!target || !target.groupId || targetReachedRef.current) {
      return;
    }

    // First, find the question that contains this answer group
    const targetQuestion = documentAnswers.find((question) =>
      question.answer_groups.some((group) => group.id === target.groupId),
    );

    if (targetQuestion) {
      setSelectedQuestionId(targetQuestion.question_id);
    }

    // Find the first citation for this group in the current document
    let targetCitationId = target.citationId;
    if (!targetCitationId) {
      const firstCitation = findFirstCitationForGroupInDocument(
        documentAnswers,
        target.groupId,
        documentId
      );
      targetCitationId = firstCitation?.id;
    }

    // Use meta index for O(1) lookup
    if (targetCitationId && scrollToHighlight.current && highlights.length > 0) {
      const highlightId = highlightMetaIndex.byCitation.get(targetCitationId);
      const targetHighlight = highlightId
        ? highlights.find((h) => h.id === highlightId)
        : undefined;

      if (targetHighlight) {
        targetReachedRef.current = true;
        scrollToHighlight.current(targetHighlight);
        onTargetReached?.();
      }
    }
  }, [target, highlights, documentAnswers, scrollToHighlight, onTargetReached, documentId, highlightMetaIndex]);

  // Clean up blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (typeof documentFile !== "string") {
        URL.revokeObjectURL(url);
      }
    };
  }, [url, documentFile]);

  // Handle highlight actions - save to backend
  const addHighlight = useCallback(
    async (
      highlight: GhostHighlight & { comment: { text: string; emoji: string } },
    ) => {
      if (!documentId || !currentUser) return;

      try {
        await savePersistentHighlight(highlight as ExtendedHighlight);
      } catch (error) {
        console.error("Failed to save highlight:", error);
        // Could add toast notification here
      }
    },
    [documentId, currentUser, savePersistentHighlight],
  );

  const updateHighlight = useCallback(
    async (
      highlightId: string,
      position: Partial<ScaledPosition>,
      content: Partial<Content>,
    ) => {
      // Check if this is a user highlight that can be edited
      if (canEditHighlight(highlightId)) {
        try {
          // Get the existing highlight to check if it's an area highlight
          const existingHighlight = highlightsRef.current.find(
            (h) => h.id === highlightId,
          );

          // Only allow dragging/repositioning for area highlights
          if (existingHighlight?.type !== "area") {
            console.warn(
              "Dragging is only allowed for area highlights:",
              highlightId,
            );
            return;
          }

          // Merge the position update with existing position data to preserve rects, etc.
          if (!existingHighlight?.position) {
            console.warn(
              "No existing highlight position found, skipping update",
            );
            return;
          }

          const updatedPosition = {
            ...existingHighlight.position,
            ...position,
          };

          await updatePersistentHighlight(highlightId, {
            position: updatedPosition,
            content: { text: content.text || "" },
          });
        } catch (error) {
          console.error("Failed to update highlight:", error);
        }
      } else {
        // Citation highlights are never editable, so this branch should never execute
        console.warn(
          "Attempted to update non-editable citation highlight:",
          highlightId,
        );
      }
    },
    [canEditHighlight, updatePersistentHighlight],
  );

  const editHighlightComment = useCallback(
    async (highlightId: string, newComment: string) => {
      // Only allow editing user highlights
      if (canEditHighlight(highlightId)) {
        try {
          await updatePersistentHighlight(highlightId, {
            comment: { text: newComment, emoji: "" },
          });
        } catch (error) {
          console.error("Failed to update highlight comment:", error);
        }
      }
    },
    [canEditHighlight, updatePersistentHighlight],
  );

  const getHighlightById = useCallback((id: string) => {
    return highlightsRef.current.find((highlight) => highlight.id === id);
  }, []);

  const scrollToHighlightFromHash = useCallback(() => {
    const highlight = getHighlightById(parseIdFromHash());
    if (highlight && scrollToHighlight.current) {
      scrollToHighlight.current(highlight);
    }
  }, [getHighlightById]);

  // Listen for hash changes to scroll to highlights when clicked in sidebar
  useEffect(() => {
    const handleHashChange = () => {
      scrollToHighlightFromHash();
    };

    window.addEventListener("hashchange", handleHashChange);

    // Also call it once on mount in case there's already a hash
    scrollToHighlightFromHash();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [scrollToHighlightFromHash]);

  // Hide tip when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only hide if tip is visible and click is outside the tip and PDF content
      if (showTip && currentSelection) {
        const target = event.target as Element;
        const tipElement = document.querySelector(".Tip__compact, .Tip__card");
        const pdfContainer = document.querySelector(".PdfHighlighter");

        // Don't hide if clicking on the tip itself or inside the PDF (where selection might be happening)
        if (tipElement && tipElement.contains(target)) {
          return;
        }
        if (pdfContainer && pdfContainer.contains(target)) {
          // Only hide if clicking on background, not on text/content
          const isTextLayer = target.closest(".textLayer");
          const isHighlight = target.closest("[data-highlight-id]");
          if (isTextLayer && !isHighlight) {
            // Clicking on text layer but not a highlight - likely deselecting
            currentSelection.hideTipAndSelection();
          }
          return;
        }

        // Clicking outside both tip and PDF - hide tip
        currentSelection.hideTipAndSelection();
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        (event.key === "Escape" || event.key === "Enter") &&
        showTip &&
        currentSelection
      ) {
        currentSelection.hideTipAndSelection();
      }
    };

    if (showTip && currentSelection) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [showTip, currentSelection]);

  // Handlers for Q&A interactions
  const handleCitationClick = useCallback(
    (citation: string, page: number | null, citationId: string) => {
      const highlightId = highlightMetaIndex.byCitation.get(citationId);
      const existingHighlight = highlightId
        ? highlightsRef.current.find((h) => h.id === highlightId)
        : undefined;

      if (existingHighlight && scrollToHighlight.current) {
        // Update active answer group from the meta index
        const meta = highlightMetaIndex.meta.get(existingHighlight.id);
        if (meta?.answerGroupIds?.[0]) {
          setActiveAnswerGroupId(meta.answerGroupIds[0]);
        }
        setActiveCitationId(citationId);
        scrollToHighlight.current(existingHighlight);
      } else if (page) {
        // Fallback: scroll to the citation's page even without a highlight.
        // Use the PDF.js page div which has data-page-number attribute.
        const container = document.querySelector(".PdfHighlighter");
        const pageEl = container?.querySelector(
          `.page[data-page-number="${page}"]`,
        );
        if (pageEl) {
          pageEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        console.warn(
          `No existing highlight found for citation: "${citation}" on page ${page}, citationId: ${citationId}`,
        );
      }
    },
    [highlightMetaIndex],
  );

  const toggleQuestionPane = useCallback(() => {
    setShowQuestionPane((prev) => !prev);
  }, []);

  const toggleAnswerPane = useCallback(() => {
    setShowAnswerPane((prev) => !prev);
  }, []);

  // Delete confirmation handlers
  const handleDeleteClick = useCallback((highlightId: string) => {
    setHighlightToDelete(highlightId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (highlightToDelete) {
      try {
        await deletePersistentHighlight(highlightToDelete);
        setDeleteDialogOpen(false);
        setHighlightToDelete(null);
      } catch (error) {
        console.error("Failed to delete highlight:", error);
      }
    }
  }, [highlightToDelete, deletePersistentHighlight]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setHighlightToDelete(null);
  }, []);

  // Filter user highlights (exclude citations and merged citations)
  const userHighlights = React.useMemo(() => {
    return persistentHighlights; // User highlights are just the persistent highlights since citations are separate
  }, [persistentHighlights]);

  // Stable callback functions for PdfHighlighter to prevent re-renders
  const enableAreaSelection = useCallback(
    (event: MouseEvent) => event.altKey,
    [],
  );

  const onSelection = useCallback((selection: any) => {
    const ghostHighlight = selection.makeGhostHighlight();

    // Only show tip if there's actual content selected
    if (
      !ghostHighlight.content?.text?.trim() &&
      !ghostHighlight.content?.image
    ) {
      return;
    }

    setCurrentSelection({
      position: ghostHighlight.position,
      content: ghostHighlight.content,
      hideTipAndSelection: () => {
        // Simple hide function
        setShowTip(false);
        setCurrentSelection(null);
      },
    });
    setShowTip(true);
    setTipCompact(true);
  }, []);

  // Ref to scope DOM queries to the PDF viewer container
  const viewerRootRef = useRef<HTMLDivElement>(null);

  const utilsRef = useCallback(
    (utils: any) => {
      scrollToHighlight.current = (highlight) => {
        resetHash();

        const doScroll = () => {
          utils.scrollToHighlight(highlight);

          // Brief DOM-level visibility override: the library's separate React
          // roots (createRoot per page) re-render asynchronously, so the
          // state-driven class may not be applied yet when the viewport scrolls.
          // data-scroll-target forces visibility via CSS until state catches up.
          const root = viewerRootRef.current ?? document;
          root.querySelectorAll("[data-scroll-target]")
            .forEach((el) => el.removeAttribute("data-scroll-target"));

          const el = root.querySelector(`[data-highlight-id="${highlight.id}"]`);
          if (el) {
            el.setAttribute("data-scroll-target", "true");
            setTimeout(() => el.removeAttribute("data-scroll-target"), SCROLL_HIGHLIGHT_DURATION_MS);
          }

          // Offset the viewport upward so the signature row ABOVE the matched
          // printed name stays visible. Queued as a smooth scroll on the same
          // tick as the library's scrollToHighlight, so the two motions
          // coalesce into one continuous slide rather than a visible jump.
          // Skip in two cases:
          //   1. The matched text is itself a signature row (label, "/s/ Name",
          //      DocuSign envelope, etc.) — the handwritten mark is beside/within
          //      it, so the offset overshoots.
          //   2. Area highlights (bbox-fallback for scanned PDFs with no
          //      native text layer) — we have no text content to reason about,
          //      and the Textract LINE bbox the backend matched is usually
          //      already the signature-context row.
          const isAreaHighlight = highlight.type === "area";
          const highlightText = highlight.content?.text ?? "";
          const highlightIsSignatureRow = HIGHLIGHT_IS_SIGNATURE_ROW_RE.test(highlightText);
          if (!isAreaHighlight && !highlightIsSignatureRow) {
            const container = root.querySelector(".PdfHighlighter");
            if (container instanceof HTMLElement) {
              container.scrollBy({ top: -SIGNATURE_CONTEXT_OFFSET_PX, behavior: "smooth" });
            }
          }
        };

        try {
          doScroll();
        } catch {
          // Page container may not be rendered yet (e.g. slow PDF load on QA).
          // Retry once after a short delay to let the page mount.
          setTimeout(() => {
            try {
              doScroll();
            } catch {
              // Still not ready — silently ignore rather than crashing the viewer
            }
          }, 500);
        }
      };
      scrollToHighlightFromHash();
    },
    [scrollToHighlightFromHash],
  );

  const handleQuestionClick = useCallback(
    (questionId: string) => {
      const question = documentAnswers.find(
        (q) => q.question_id === questionId,
      );

      setSelectedQuestionId(questionId);
      if (question) {
        const currentValueGroup = question.answer_groups.find((g) => g.is_current_value);
        const targetGroup = currentValueGroup ?? question.answer_groups[0];
        if (targetGroup) {
          setActiveAnswerGroupId(targetGroup.id);

          const firstCitation = findFirstCitationForGroupInDocument(
            documentAnswers,
            targetGroup.id,
            documentId
          );

          if (firstCitation) {
            // O(1) lookup via meta index, scroll immediately — the DOM-level
            // override in scrollToHighlight handles visibility during the gap
            // before the library's async roots commit the state change
            const highlightId = highlightMetaIndex.byCitation.get(firstCitation.id);
            const highlight = highlightId
              ? highlightsRef.current.find((h) => h.id === highlightId)
              : undefined;
            if (highlight && scrollToHighlight.current) {
              scrollToHighlight.current(highlight);
            }
          }
        }
      }
    },
    [documentAnswers, documentId, highlightMetaIndex],
  );

  return (
    <>
      <style>{`
        .PdfHighlighter {
        }
        .Highlight {
        }
        .Highlight__popup {
          max-height: none !important;
          overflow: visible !important;
          height: auto !important;
        }
        /* Citation highlight styles — active tier (selected answer group) */
        .citation-highlight-active .TextHighlight__part {
          background-color: rgba(34, 197, 94, 0.35) !important;
          border: 1px solid rgb(22, 163, 74) !important;
          pointer-events: auto !important;
          z-index: 2;
        }
        .citation-highlight-active .AreaHighlight__part,
        .citation-highlight-active .AreaHighlight {
          background-color: rgba(34, 197, 94, 0.35) !important;
          border: 1px solid rgb(22, 163, 74) !important;
          pointer-events: auto !important;
          z-index: 2;
        }
        /* Citation highlight styles — background tier (same question, different group / show-all) */
        .citation-highlight-background .TextHighlight__part {
          background-color: rgba(34, 197, 94, 0.10) !important;
          border: 1px solid rgba(22, 163, 74, 0.3) !important;
          pointer-events: auto !important;
          z-index: 1;
        }
        .citation-highlight-background .AreaHighlight__part,
        .citation-highlight-background .AreaHighlight {
          background-color: rgba(34, 197, 94, 0.10) !important;
          border: 1px solid rgba(22, 163, 74, 0.3) !important;
          pointer-events: auto !important;
          z-index: 1;
        }
        /* Hidden tier — in DOM for scroll targeting but invisible */
        .citation-highlight-hidden {
          opacity: 0 !important;
          pointer-events: none !important;
        }
        .citation-highlight-hidden .TextHighlight__part,
        .citation-highlight-hidden .AreaHighlight__part,
        .citation-highlight-hidden .AreaHighlight {
          opacity: 0 !important;
          pointer-events: none !important;
        }
        /* Brief DOM-level override during scroll — forces hidden highlights
           visible while the library's async React roots catch up to state.
           Auto-removed after 1.5s by the scroll wrapper. */
        [data-scroll-target="true"] {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        [data-scroll-target="true"] .TextHighlight__part {
          background-color: rgba(34, 197, 94, 0.35) !important;
          border: 1px solid rgb(22, 163, 74) !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          z-index: 2;
        }
        [data-scroll-target="true"] .AreaHighlight__part,
        [data-scroll-target="true"] .AreaHighlight {
          background-color: rgba(34, 197, 94, 0.35) !important;
          border: 1px solid rgb(22, 163, 74) !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          z-index: 2;
        }
        .user-highlight .TextHighlight__part,
        .user-highlight .Highlight__part,
        .user-highlight [data-highlight-id] > div {
          background-color: rgba(255, 200, 120, 0.35) !important;
          border: 1px solid rgb(249, 115, 22) !important;
        }
        .user-highlight .AreaHighlight__part,
        .user-highlight .AreaHighlight {
          background-color: rgba(255, 200, 120, 0.35) !important;
          border: 1px solid rgb(249, 115, 22) !important;
        }
        /* Style for non-draggable area highlights */
        .user-highlight .AreaHighlight__part:not(.react-draggable) {
          cursor: default !important;
        }
        /* Hover effects for user highlights */
        .user-highlight .TextHighlight__part:hover {
          background-color: rgba(249, 115, 22, 0.35) !important;
          border-color: rgb(194, 65, 12) !important;
          transition: all 0.2s ease-in-out;
        }
        .user-highlight .AreaHighlight__part:hover {
          background-color: rgba(249, 115, 22, 0.35) !important;
          border-color: rgb(194, 65, 12) !important;
          transition: all 0.2s ease-in-out;
        }
        /* Selection colors to match user highlights (orange) */
        .PdfHighlighter ::selection {
          background-color: rgba(249, 115, 22, 0.4) !important;
        }
        /* Flash animation for scrolled-to highlights */
        @keyframes highlight-flash-citation {
          0% { 
            background-color: rgba(240, 253, 244, 0.8) !important;
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 1);
          }
          20% { 
            background-color: rgba(34, 197, 94, 0.9) !important;
            box-shadow: 0 0 0 3px rgba(34, 197, 94, 1);
          }
          40% { 
            background-color: rgba(34, 197, 94, 1) !important;
            box-shadow: 0 0 0 6px rgba(34, 197, 94, 1);
          }
          60% { 
            background-color: rgba(34, 197, 94, 1) !important;
            box-shadow: 0 0 0 8px rgba(34, 197, 94, 1);
          }
          80% { 
            background-color: rgba(34, 197, 94, 0.9) !important;
            box-shadow: 0 0 0 4px rgba(34, 197, 94, 1);
          }
          100% { 
            background-color: rgba(240, 253, 244, 0.8) !important;
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 1);
          }
        }
        @keyframes highlight-flash-user {
          0% { 
            background-color: rgb(255, 200, 120) !important;
            box-shadow: 0 0 0 0 rgba(249, 115, 22, 1);
          }
          20% { 
            background-color: rgba(249, 115, 22, 0.9) !important;
            box-shadow: 0 0 0 3px rgba(249, 115, 22, 1);
          }
          40% { 
            background-color: rgba(249, 115, 22, 1) !important;
            box-shadow: 0 0 0 6px rgba(249, 115, 22, 1);
          }
          60% { 
            background-color: rgba(249, 115, 22, 1) !important;
            box-shadow: 0 0 0 8px rgba(249, 115, 22, 1);
          }
          80% { 
            background-color: rgba(249, 115, 22, 0.9) !important;
            box-shadow: 0 0 0 4px rgba(249, 115, 22, 1);
          }
          100% { 
            background-color: rgb(255, 200, 120) !important;
            box-shadow: 0 0 0 0 rgba(249, 115, 22, 1);
          }
        }
        .highlight-flash-citation .TextHighlight__part,
        .highlight-flash-citation .AreaHighlight__part {
          animation: highlight-flash-citation 1.2s ease-in-out;
        }
        .highlight-flash-user .TextHighlight__part,
        .highlight-flash-user .AreaHighlight__part {
          animation: highlight-flash-user 1.2s ease-in-out;
        }
        .mouseSelection {
          background-color: rgba(249, 115, 22, 0.6) !important;
          border: 1px dashed rgb(249, 115, 22) !important;
          mix-blend-mode: normal !important;
        }
      `}</style>
      <div className="flex h-full min-h-0">
        {/* Left Pane - Questions */}
        {!readOnly && (
          <QuestionPane
            selectedQuestionId={selectedQuestionId}
            questions={documentAnswers}
            userHighlights={userHighlights}
            isVisible={showQuestionPane}
            onToggle={toggleQuestionPane}
            onQuestionClick={handleQuestionClick}
            onHighlightClick={(highlight) => {
              // Clear selected question when clicking on user highlights
              setSelectedQuestionId(undefined);

              if (scrollToHighlight.current) {
                scrollToHighlight.current(highlight as ExtendedHighlight);
              }
            }}
            canEditHighlight={canEditHighlight}
            onEditHighlight={editHighlightComment}
            onDeleteClick={handleDeleteClick}
          />
        )}
        {/* Center Pane - Document */}
        <div
          ref={viewerRootRef}
          className={`relative h-full min-h-0 ${
            readOnly
              ? "w-full"
              : showQuestionPane && showAnswerPane
                ? "w-1/2"
                : showQuestionPane || showAnswerPane
                  ? "w-3/4"
                  : "w-full"
          }`}
        >
          {/* Show All Highlights toggle */}
          {!readOnly && (
            <button
              onClick={() => setShowAllHighlights((prev) => !prev)}
              className={`absolute right-3 top-3 z-10 rounded-md border px-2.5 py-1 text-xs transition-colors ${
                showAllHighlights
                  ? "border-green-400 bg-green-50 text-green-800"
                  : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={showAllHighlights ? "Showing all highlights" : "Show only selected question's highlights"}
            >
              {showAllHighlights ? "All highlights" : "Focused"}
            </button>
          )}
          <PdfLoader
            document={url}
            beforeLoad={() => <LoadingSpinner />}
            workerSrc={workerSrc}
          >
            {(pdfDocument) => {
              // Store the PDF document reference for text search (using ref to avoid hook issues)
              if (pdfDocument) {
                setPdfDocument(pdfDocument as unknown as PDFDocumentProxy);
                // Use a flag to prevent multiple calls
                if (!pdfDocumentLoaded) {
                  // Schedule state update to avoid setState during render
                  Promise.resolve().then(() => setPdfDocumentLoaded(true));
                }
              }

              return (
                <PdfHighlighter
                  highlights={readOnly ? [] : highlights}
                  pdfDocument={pdfDocument}
                  enableAreaSelection={
                    readOnly ? undefined : enableAreaSelection
                  }
                  onScrollAway={resetHash}
                  onSelection={readOnly ? () => false : onSelection}
                  utilsRef={utilsRef}
                >
                  <HighlightRenderer
                    citationDataMap={citationDataMap}
                    highlightMetaIndex={highlightMetaIndex}
                    canEditHighlight={canEditHighlight}
                    onDeleteClick={handleDeleteClick}
                    editHighlightComment={editHighlightComment}
                    updateHighlight={updateHighlight}
                    selectedQuestionId={selectedQuestionId}
                    activeAnswerGroupId={activeAnswerGroupId}
                    activeCitationId={activeCitationId}
                    showAllHighlights={showAllHighlights}
                  />
                </PdfHighlighter>
              );
            }}
          </PdfLoader>
        </div>
        {/* Right Pane - Answers */}
        {!readOnly && (
          <AnswerPane
            questions={documentAnswers}
            selectedQuestionId={selectedQuestionId}
            isVisible={showAnswerPane}
            onToggle={toggleAnswerPane}
            onCitationClick={handleCitationClick}
            documentId={documentId}
            target={target}
          />
        )}
        {/* Render tip separately */}
        {showTip &&
          currentSelection &&
          (() => {
            // Calculate position based on selection in viewport coordinates
            const selectionRect = currentSelection.position.boundingRect;

            // Find the PDF container to get proper positioning
            const pdfContainer = document.querySelector(".PdfHighlighter");
            const pdfPage = document.querySelector(
              `[data-page-number="${currentSelection.position.boundingRect.pageNumber}"]`,
            );

            let tipPosition = { x: 0, y: 0 };

            if (pdfContainer && pdfPage) {
              const pageRect = pdfPage.getBoundingClientRect();

              // Calculate the scale factor between PDF coordinates and rendered page
              const scale = pageRect.width / selectionRect.width;

              // Convert PDF coordinates to screen coordinates
              const screenX = pageRect.left + selectionRect.x1 * scale;
              const screenY = pageRect.top + selectionRect.y1 * scale;

              tipPosition = {
                x:
                  screenX + ((selectionRect.x2 - selectionRect.x1) * scale) / 2, // Center horizontally
                y: screenY, // Top of selection
              };
            } else {
              // Fallback positioning
              tipPosition = {
                x: selectionRect.x1 + (selectionRect.x2 - selectionRect.x1) / 2,
                y: selectionRect.y1,
              };
            }

            return (
              <CustomTip
                compact={tipCompact}
                position={tipPosition}
                onOpen={() => {
                  setTipCompact(false);
                }}
                onConfirm={(comment) => {
                  const highlightWithComment = {
                    content: currentSelection.content,
                    position: currentSelection.position,
                    type: "text" as const,
                    comment: comment,
                  };

                  // Include comment when adding highlight
                  addHighlight(highlightWithComment);
                  currentSelection.hideTipAndSelection();
                  setShowTip(false);
                  setTipCompact(true);
                  setCurrentSelection(null);
                }}
                onClose={() => {
                  currentSelection.hideTipAndSelection();
                  setShowTip(false);
                  setTipCompact(true);
                  setCurrentSelection(null);
                }}
              />
            );
          })()}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="z-[3000]" overlayClassname="z-[2999]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Highlight</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this highlight? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleDeleteCancel}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}
